import type { CurrencyDto } from '@shared/api/contracts';

export type CurrencyAssetKind = 'stable' | 'native';
export type NetworkFeeLevel = 'low' | 'high';
export type CurrencyListFilter = 'all' | 'stable' | 'native';

export interface CurrencyNetworkOption {
  id: number;
  code: string;
  label: string;
  fullName: string;
  fee: NetworkFeeLevel;
  icon?: string;
}

export interface CurrencyAssetGroup {
  code: string;
  name: string;
  kind: CurrencyAssetKind;
  icon?: string;
  privacy: boolean;
  options: CurrencyNetworkOption[];
  nativeNetworkName: string;
}

const PRIVACY_ASSETS = new Set(['XMR', 'ZEC']);

const HIGH_FEE_NETWORKS = new Set(['ERC20', 'ETH', 'ETHEREUM']);

const NETWORK_LABELS: Record<string, string> = {
  TRC20: 'TRC-20',
  BEP20: 'BEP-20',
  ERC20: 'ERC-20',
  SOL: 'Solana',
  SOLANA: 'Solana',
  ARB: 'Arbitrum',
  ARBITRUM: 'Arbitrum',
  RH: 'Robinhood',
  ROBINHOOD: 'Robinhood',
};

const NATIVE_ALIASES: Record<string, string[]> = {
  BTC: ['BITCOIN'],
  LTC: ['LITECOIN'],
  ETH: ['ETHEREUM', 'ETHER'],
  SOL: ['SOLANA'],
  TRX: ['TRON'],
  BNB: ['BSC', 'BINANCE', 'BINANCESMARTCHAIN'],
  XMR: ['MONERO'],
  ZEC: ['ZCASH'],
  TON: ['TONCOIN'],
  DOGE: ['DOGECOIN'],
};

const normalize = (value: string) => value.trim().toUpperCase().replace(/[\s_-]+/g, '');

const assetCodeOf = (currency: CurrencyDto) =>
  (currency.short_code || currency.code || String(currency.id)).trim().toUpperCase();

const isNativeChain = (asset: string, currency: CurrencyDto) => {
  const networkCode = currency.network?.code ?? '';
  const networkName = currency.network?.name ?? '';
  if (!networkCode && !networkName) return true;

  const assetKey = normalize(asset);
  const codeKey = normalize(networkCode);
  const nameKey = normalize(networkName);
  if (codeKey === assetKey || nameKey === assetKey) return true;

  const aliases = NATIVE_ALIASES[assetKey] ?? [];
  return aliases.some((alias) => alias === codeKey || alias === nameKey);
};

const feeOf = (networkCode: string): NetworkFeeLevel =>
  HIGH_FEE_NETWORKS.has(normalize(networkCode)) ? 'high' : 'low';

const networkLabel = (code: string, name?: string) => {
  const key = normalize(code);
  if (NETWORK_LABELS[key]) return NETWORK_LABELS[key];
  if (name && normalize(name) !== key) return name;
  return code || name || '';
};

const toOption = (currency: CurrencyDto): CurrencyNetworkOption => {
  const code = currency.network?.code || currency.code;
  const name = currency.network?.name;
  const label = networkLabel(code, name);
  return {
    id: currency.id,
    code,
    label,
    fullName: name || label,
    fee: feeOf(code),
    icon: currency.icon_network || undefined,
  };
};

/** Каталог с бэка. `enable: false` — валюта не принимается и в выбор не попадает. */
export const acceptedCurrencies = (currencies: CurrencyDto[]) =>
  currencies.filter((currency) => currency.enable !== false);

export const groupCurrencies = (currencies: CurrencyDto[]): CurrencyAssetGroup[] => {
  const order: string[] = [];
  const grouped = new Map<string, CurrencyDto[]>();

  currencies.forEach((currency) => {
    const code = assetCodeOf(currency);
    if (!grouped.has(code)) {
      order.push(code);
      grouped.set(code, []);
    }
    grouped.get(code)?.push(currency);
  });

  const groups = order.map((code) => {
    const items = grouped.get(code) ?? [];
    const kind: CurrencyAssetKind = items.every((item) => isNativeChain(code, item))
      ? 'native'
      : 'stable';
    const first = items[0];

    return {
      code,
      name: items.find((item) => item.name)?.name || code,
      kind,
      icon: items.find((item) => item.icon_base)?.icon_base,
      privacy: PRIVACY_ASSETS.has(code),
      options: items.map(toOption),
      nativeNetworkName: first?.network?.name || first?.network?.code || code,
    };
  });

  return [
    ...groups.filter((group) => group.kind === 'stable'),
    ...groups.filter((group) => group.kind === 'native'),
  ];
};

export const filterCurrencyGroups = (
  groups: CurrencyAssetGroup[],
  filter: CurrencyListFilter,
  query: string,
) => {
  const needle = query.trim().toLowerCase();

  return groups.filter((group) => {
    if (filter !== 'all' && group.kind !== filter) return false;
    if (!needle) return true;

    const haystack = [
      group.code,
      group.name,
      group.nativeNetworkName,
      ...group.options.flatMap((option) => [option.label, option.fullName, option.code]),
    ]
      .join(' ')
      .toLowerCase();

    return haystack.includes(needle);
  });
};

const optionIds = (group: CurrencyAssetGroup) => group.options.map((option) => option.id);

export const isGroupEnabled = (
  group: CurrencyAssetGroup,
  selectedIds: number[],
  openWithoutNetworks: ReadonlySet<string>,
) =>
  openWithoutNetworks.has(group.code) ||
  group.options.some((option) => selectedIds.includes(option.id));

export const toggleAsset = (
  groups: CurrencyAssetGroup[],
  selectedIds: number[],
  assetCode: string,
  enabled: boolean,
) => {
  const group = groups.find((item) => item.code === assetCode);
  if (!group) return selectedIds;

  const owned = new Set(optionIds(group));
  const rest = selectedIds.filter((id) => !owned.has(id));
  if (!enabled) return rest;

  if (group.kind === 'native') return [...rest, ...optionIds(group)];

  const already = group.options.filter((option) => selectedIds.includes(option.id));
  if (already.length) return selectedIds;

  const lowFee = group.options.filter((option) => option.fee === 'low');
  const pick = lowFee.length ? lowFee : group.options;
  return [...rest, ...pick.map((option) => option.id)];
};

export const toggleNetwork = (
  groups: CurrencyAssetGroup[],
  selectedIds: number[],
  assetCode: string,
  networkId: number,
) => {
  const group = groups.find((item) => item.code === assetCode);
  if (!group) return { selectedIds, emptied: false };

  const has = selectedIds.includes(networkId);
  const next = has
    ? selectedIds.filter((id) => id !== networkId)
    : [...selectedIds, networkId];
  const left = group.options.some((option) => next.includes(option.id));

  return { selectedIds: next, emptied: !left };
};

export const selectAllCurrencies = (groups: CurrencyAssetGroup[], enabled: boolean) =>
  enabled ? groups.flatMap((group) => optionIds(group)) : [];

export const isAllCurrenciesSelected = (groups: CurrencyAssetGroup[], selectedIds: number[]) =>
  groups.length > 0 &&
  groups.every((group) => group.options.every((option) => selectedIds.includes(option.id)));

export const selectionSummary = (
  groups: CurrencyAssetGroup[],
  selectedIds: number[],
  openWithoutNetworks: ReadonlySet<string>,
) => {
  let coins = 0;
  let options = 0;
  const missing: string[] = [];

  groups.forEach((group) => {
    const selected = group.options.filter((option) => selectedIds.includes(option.id));
    const armed = openWithoutNetworks.has(group.code);
    if (!selected.length && !armed) return;

    coins += 1;
    if (group.kind === 'native') {
      options += selected.length > 0 ? 1 : 0;
      return;
    }

    options += selected.length;
    if (!selected.length) missing.push(group.code);
  });

  return { coins, options, missing, total: groups.length };
};
